import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import TableRestaurantIcon from '@mui/icons-material/TableRestaurant';
import RoomServiceIcon from '@mui/icons-material/RoomService';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import PaymentsIcon from '@mui/icons-material/Payments';
import Lottie from 'lottie-react';
import { useLocation, useNavigate } from 'react-router-dom';
import foodVloggerAnimation from '../../../assets/lottie/food-vlogger.json';
import { createPosOpenQrOrder, getPosOpenQrBootstrap } from '../api';
import { detectThemeSync, watchThemeChanges } from '../../../utils/themeUtils';
import {
  clearOpenOrderSession,
  formatCurrency,
  getCartRows,
  getOpenOrderBillSummary,
  getOpenOrderSession,
  resolvePrepTimeMinutes,
  sessionMinutesToPrepUiState,
  updateOpenOrderSession,
} from '../openOrderUtils';

const ACCENT = '#4ce1b6';
const OCCUPIED_TABLE_POPUP_IMAGE = '/static/occupied-table-popup.png';
const EMPTY_CUSTOMER_FORM = {
  customer_name: '',
  customer_phone_country_code: '+91',
  customer_phone: '',
  notes: '',
};

const isValidIndianMobileNumber = (value) => /^[6-9]\d{9}$/.test(String(value || '').replace(/\D/g, ''));

export default function PosOpenOrderingCheckoutPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [currentTheme, setCurrentTheme] = useState(() => detectThemeSync());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [bootstrap, setBootstrap] = useState(null);
  const [cart, setCart] = useState({});
  const [customerForm, setCustomerForm] = useState(EMPTY_CUSTOMER_FORM);
  const [submittingOrder, setSubmittingOrder] = useState(false);
  const [sessionLoaded, setSessionLoaded] = useState(false);
  const [prepChoice, setPrepChoice] = useState('30');
  const [prepCustom, setPrepCustom] = useState('30');
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [orderPlacedSuccess, setOrderPlacedSuccess] = useState(false);
  const [occupiedDialogOpen, setOccupiedDialogOpen] = useState(false);
  const isDark = currentTheme === 'dark';

  useEffect(() => {
    const cleanup = watchThemeChanges((theme) => setCurrentTheme(theme));
    return cleanup;
  }, []);

  const queryParams = useMemo(() => {
    const q = new URLSearchParams(location.search);
    return {
      hotelId: q.get('hotel_id') || '',
      targetType: q.get('target_type') || '',
      targetId: q.get('target_id') || '',
      qrToken: q.get('qr_token') || '',
    };
  }, [location.search]);

  const loadBootstrap = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const payload = await getPosOpenQrBootstrap(queryParams);
      setBootstrap(payload);
    } catch (err) {
      setError(err?.userMessage || 'Unable to load checkout page.');
    } finally {
      setLoading(false);
    }
  }, [queryParams]);

  useEffect(() => {
    loadBootstrap();
  }, [loadBootstrap]);

  useEffect(() => {
    const session = getOpenOrderSession(queryParams);
    setCart(session.cart || {});
    setCustomerForm(session.customerForm || EMPTY_CUSTOMER_FORM);
    const ui = sessionMinutesToPrepUiState(session.preparationTimeMinutes);
    setPrepChoice(ui.prepChoice);
    setPrepCustom(ui.prepCustom);
    setSessionLoaded(true);
  }, [queryParams]);

  const preparationTimeMinutes = useMemo(
    () => resolvePrepTimeMinutes(prepChoice, prepCustom),
    [prepChoice, prepCustom],
  );

  useEffect(() => {
    if (!sessionLoaded) return;
    updateOpenOrderSession(queryParams, { cart, customerForm, preparationTimeMinutes });
  }, [cart, customerForm, preparationTimeMinutes, queryParams, sessionLoaded]);

  /** Room QR: auto-fill name/phone from active booking guest when fields are still empty. */
  useEffect(() => {
    if (!bootstrap || !sessionLoaded) return;
    const pre = bootstrap.guest_prefill;
    if (!pre || bootstrap.target_type !== 'room') return;

    setCustomerForm((prev) => {
      const nameEmpty = !String(prev.customer_name || '').trim();
      const phoneEmpty = !String(prev.customer_phone || '').trim();
      if (!nameEmpty && !phoneEmpty) return prev;
      const phoneDigits = String(pre.customer_phone || '').replace(/\D/g, '').slice(0, 10);
      return {
        ...prev,
        customer_name: nameEmpty ? String(pre.customer_name || '').trim() : prev.customer_name,
        customer_phone_country_code: pre.customer_phone_country_code || prev.customer_phone_country_code || '+91',
        customer_phone: phoneEmpty ? phoneDigits : prev.customer_phone,
      };
    });
  }, [bootstrap, sessionLoaded]);

  const cartRows = useMemo(() => getCartRows(cart), [cart]);
  const hasCartItems = cartRows.length > 0;
  const billSummary = useMemo(
    () => getOpenOrderBillSummary(cart, bootstrap?.settings),
    [bootstrap, cart]
  );
  const isPhoneValid = useMemo(
    () => isValidIndianMobileNumber(customerForm.customer_phone),
    [customerForm.customer_phone]
  );

  const phoneDigits = String(customerForm.customer_phone || '').replace(/\D/g, '');
  const phoneHelperText = useMemo(() => {
    if (!phoneTouched) return 'Required to place the order.';
    if (!phoneDigits.length) return 'Enter your 10-digit mobile number.';
    if (!isPhoneValid) return 'Enter a valid 10-digit Indian mobile number (starts with 6–9).';
    return '';
  }, [phoneTouched, phoneDigits.length, isPhoneValid]);

  const updateCartQty = useCallback((rowKey, delta) => {
    setCart((prev) => {
      const current = prev[rowKey];
      if (!current) return prev;
      const nextQty = Math.max(0, Number(current.quantity || 0) + delta);
      if (nextQty === 0) {
        const { [rowKey]: _removed, ...rest } = prev;
        return rest;
      }
      return {
        ...prev,
        [rowKey]: {
          ...current,
          quantity: nextQty,
        },
      };
    });
  }, []);

  const handlePlaceOrder = useCallback(async () => {
    setPhoneTouched(true);
    if (!hasCartItems) {
      setError('Your cart is empty.');
      return;
    }
    if (!isPhoneValid) {
      setError('Please enter a valid mobile number to place the order.');
      return;
    }

    setSubmittingOrder(true);
    setError('');
    try {
      const payload = {
        hotel_id: queryParams.hotelId,
        target_type: queryParams.targetType,
        target_id: queryParams.targetId,
        qr_token: queryParams.qrToken,
        customer_name: customerForm.customer_name,
        customer_phone_country_code: customerForm.customer_phone_country_code,
        customer_phone: customerForm.customer_phone,
        notes: customerForm.notes,
        preparation_time_minutes: preparationTimeMinutes,
        order_items: cartRows.map((row) => ({
          menu_item: row.menuItemId,
          variation_id: row.variationId,
          quantity: row.quantity,
          special_note: '',
        })),
      };
      await createPosOpenQrOrder(payload);
      clearOpenOrderSession(queryParams);
      setCart({});
      setCustomerForm({ ...EMPTY_CUSTOMER_FORM });
      setOrderPlacedSuccess(true);
    } catch (err) {
      if (err?.responseData?.code === 'table_occupied') {
        setOccupiedDialogOpen(true);
        setError('');
      } else {
        setError(err?.userMessage || 'Unable to place order. Please try again.');
      }
    } finally {
      setSubmittingOrder(false);
    }
  }, [cartRows, customerForm, hasCartItems, isPhoneValid, location.search, navigate, preparationTimeMinutes, queryParams]);

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', bgcolor: isDark ? '#0b1220' : '#f8fbff' }}>
        <Stack spacing={1} alignItems="center">
          <CircularProgress size={34} />
          <Typography variant="body2" color="text.secondary">Loading checkout...</Typography>
        </Stack>
      </Box>
    );
  }

  if (error && !bootstrap) {
    return (
      <Box sx={{ minHeight: '100vh', p: 2.4, bgcolor: isDark ? '#0b1220' : '#f8fbff' }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!bootstrap) return null;

  if (orderPlacedSuccess) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: isDark ? '#0b1220' : '#f3fbfa',
          color: isDark ? '#eaf6ff' : '#13233b',
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            px: { xs: 2, sm: 3 },
            py: { xs: 2, sm: 2 },
          }}
        >
          <Box
            sx={{
              width: 'min(92vw, 340px)',
              maxWidth: 340,
              mx: 'auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Lottie
              animationData={foodVloggerAnimation}
              loop
              style={{ width: '100%', height: 'auto' }}
            />
          </Box>
          <Typography
            sx={{
              mt: 2.5,
              mb: 0.5,
              fontWeight: 800,
              fontSize: { xs: '1.2rem', sm: '1.35rem' },
              textAlign: 'center',
              px: 1,
            }}
          >
            Order placed successfully!
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: isDark ? '#9fbbd2' : '#607086',
              textAlign: 'center',
              maxWidth: 400,
              px: 2,
            }}
          >
            Thank you — the kitchen has received your order.
          </Typography>
        </Box>
        <Box
          sx={{
            width: '100%',
            maxWidth: 480,
            mx: 'auto',
            px: { xs: 2, sm: 3 },
            pb: { xs: 'calc(20px + env(safe-area-inset-bottom, 0px))', sm: 3 },
            pt: 0.5,
          }}
        >
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={() => navigate(
              { pathname: '/pos-restaurant/open/', search: location.search },
              { replace: true },
            )}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              minHeight: 50,
              fontSize: '1rem',
              borderRadius: 2,
              background: `linear-gradient(90deg, ${ACCENT} 0%, #2dd4a0 100%)`,
              color: '#06251e',
              boxShadow: `0 4px 16px ${alpha(ACCENT, 0.35)}`,
              '&:hover': {
                background: `linear-gradient(90deg, #3dd1a8 0%, #22c997 100%)`,
              },
            }}
          >
            Return to menu
          </Button>
        </Box>
      </Box>
    );
  }

  const hotelImageUrl = String(bootstrap?.hotel?.image_url || '').trim();
  const hotelImageAlt = String(bootstrap?.hotel?.name || 'Hotel').trim() || 'Hotel';
  const targetType = bootstrap?.target_type;
  const target = bootstrap?.target || {};
  const targetLabel = targetType === 'room'
    ? `Room ${target?.room_number || '-'}`
    : (target?.name || `Table ${target?.id || '-'}`);
  const cgstPct = Number(billSummary?.cgstRate ?? 0);
  const sgstPct = Number(billSummary?.sgstRate ?? 0);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        px: { xs: 1, sm: 1.8, md: 3, lg: 4 },
        pt: 0,
        pb: hasCartItems
          ? {
            xs: 'calc(98px + env(safe-area-inset-bottom, 0px))',
            sm: 'calc(108px + env(safe-area-inset-bottom, 0px))',
          }
          : { xs: 2.4, sm: 3 },
        bgcolor: isDark ? '#0b1220' : '#f3fbfa',
        color: isDark ? '#eaf6ff' : '#13233b',
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: { xs: 620, md: 'min(1600px, 100%)' },
          mx: 'auto',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            mx: { xs: -1, sm: -1.8, md: -3, lg: -4 },
            p: { xs: 1.2, sm: 1.5 },
            borderRadius: 0,
            border: `1px solid ${alpha(ACCENT, 0.34)}`,
            borderLeft: 'none',
            borderRight: 'none',
            background: isDark
              ? `linear-gradient(160deg, ${alpha('#07192b', 0.96)} 20%, ${alpha(ACCENT, 0.2)} 100%)`
              : `linear-gradient(160deg, ${alpha('#ffffff', 0.98)} 20%, ${alpha(ACCENT, 0.22)} 100%)`,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.35} justifyContent="space-between" sx={{ width: '100%' }}>
            <Box
              sx={{
                flex: '0 1 auto',
                minWidth: 0,
                maxWidth: { xs: 250, sm: 270, md: 310 },
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {hotelImageUrl ? (
                <Box
                  component="img"
                  src={hotelImageUrl}
                  alt={hotelImageAlt}
                  sx={{
                    display: 'block',
                    width: '100%',
                    height: { xs: 64, sm: 72, md: 80 },
                    objectFit: 'contain',
                    objectPosition: 'center',
                    borderRadius: 1.5,
                    border: `1px solid ${alpha(ACCENT, 0.38)}`,
                    flexShrink: 0,
                    boxShadow: isDark ? '0 4px 18px rgba(0,0,0,0.4)' : '0 2px 14px rgba(0,0,0,0.1)',
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: '100%',
                    height: { xs: 64, sm: 72, md: 80 },
                    borderRadius: 1.5,
                    border: `1px dashed ${alpha(ACCENT, 0.45)}`,
                    bgcolor: isDark ? alpha(ACCENT, 0.08) : alpha(ACCENT, 0.14),
                    display: 'grid',
                    placeItems: 'center',
                    flexShrink: 0,
                  }}
                  aria-label={hotelImageAlt}
                >
                  {targetType === 'room' ? (
                    <RoomServiceIcon sx={{ fontSize: 44, color: ACCENT, opacity: 0.9 }} />
                  ) : (
                    <TableRestaurantIcon sx={{ fontSize: 44, color: ACCENT, opacity: 0.9 }} />
                  )}
                </Box>
              )}
            </Box>

            <Chip
              icon={targetType === 'room'
                ? <RoomServiceIcon sx={{ fontSize: { xs: 26, sm: 30 } }} />
                : <TableRestaurantIcon sx={{ fontSize: { xs: 26, sm: 30 } }} />}
              label={targetLabel}
              sx={{
                height: { xs: 56, sm: 62 },
                flexShrink: 0,
                ml: 'auto',
                bgcolor: isDark ? alpha('#0f172a', 0.78) : alpha('#ffffff', 0.92),
                border: `1px solid ${alpha(ACCENT, 0.42)}`,
                fontWeight: 800,
                fontSize: { xs: '1.08rem', sm: '1.18rem' },
                borderRadius: 2,
                '& .MuiChip-label': {
                  px: { xs: 1.15, sm: 1.45 },
                  maxWidth: { xs: 200, sm: 280 },
                },
              }}
            />
          </Stack>
        </Paper>

        <Stack spacing={1.2} sx={{ mt: 1.2 }}>
          <Button
            variant="text"
            startIcon={<ArrowBackRoundedIcon />}
            onClick={() => navigate({ pathname: '/pos-restaurant/open/', search: location.search })}
            sx={{
              alignSelf: 'flex-start',
              px: 0.4,
              textTransform: 'none',
              fontWeight: 700,
              color: isDark ? '#d5fff0' : '#0f5137',
            }}
          >
            Back to menu
          </Button>

          {error ? <Alert severity="error">{error}</Alert> : null}

          {!hasCartItems ? (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                border: `1px solid ${alpha(ACCENT, 0.24)}`,
                bgcolor: isDark ? alpha('#111827', 0.9) : '#ffffff',
              }}
            >
              <Stack spacing={1}>
                <Typography sx={{ fontWeight: 800 }}>Your cart is empty</Typography>
                <Typography variant="body2" sx={{ color: isDark ? '#9fbbd2' : '#607086' }}>
                  Add at least one item from the menu to continue to checkout.
                </Typography>
              </Stack>
            </Paper>
          ) : (
            <Stack spacing={1.4}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 1.3, sm: 1.6 },
                  borderRadius: 2,
                  border: `1px solid ${alpha(ACCENT, 0.24)}`,
                  bgcolor: isDark ? alpha('#111827', 0.9) : '#ffffff',
                }}
              >
                <Stack spacing={1.1}>
                  <Typography sx={{ fontWeight: 800, fontSize: 18 }}>Selected items</Typography>
                  {cartRows.map((row) => (
                    <Box
                      key={row.key}
                      sx={{
                        p: 1,
                        borderRadius: 1.6,
                        border: `1px solid ${alpha(ACCENT, 0.18)}`,
                        bgcolor: isDark ? alpha('#0b1220', 0.68) : alpha('#f8fffc', 0.95),
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 700 }}>{row.name}</Typography>
                          {row.variationLabel ? (
                            <Typography variant="caption" sx={{ color: isDark ? '#9fbbd2' : '#607086', display: 'block' }}>
                              Variation: {row.variationLabel}
                            </Typography>
                          ) : null}
                          <Typography variant="caption" sx={{ color: isDark ? '#9fbbd2' : '#607086' }}>
                            {formatCurrency(row.unitPrice, bootstrap?.hotel?.currency)} × {row.quantity} ={' '}
                            {formatCurrency(
                              Number(row.unitPrice || 0) * Number(row.quantity || 0),
                              bootstrap?.hotel?.currency
                            )}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={0.2} alignItems="center" sx={{ flexShrink: 0 }}>
                          <IconButton size="small" onClick={() => updateCartQty(row.key, -1)} aria-label="Decrease quantity">
                            <RemoveIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                          <Typography sx={{ minWidth: 24, textAlign: 'center', fontWeight: 800 }}>
                            {row.quantity}
                          </Typography>
                          <IconButton size="small" onClick={() => updateCartQty(row.key, 1)} aria-label="Increase quantity">
                            <AddIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Stack>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  p: { xs: 1.3, sm: 1.6 },
                  borderRadius: 2,
                  border: `1px solid ${alpha(ACCENT, 0.24)}`,
                  bgcolor: isDark ? alpha('#111827', 0.9) : '#ffffff',
                }}
              >
                <Stack spacing={1.15}>
                  <Typography sx={{ fontWeight: 800, fontSize: 18 }}>Bill summary</Typography>
                  <Stack direction="row" justifyContent="space-between" gap={1}>
                    <Typography variant="body2" sx={{ color: isDark ? '#9fbbd2' : '#607086' }}>Item subtotal</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {formatCurrency(Number(billSummary?.subtotal ?? 0), bootstrap?.hotel?.currency)}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" gap={1}>
                    <Typography variant="body2" sx={{ color: isDark ? '#9fbbd2' : '#607086' }}>
                      CGST ({Number.isFinite(cgstPct) ? cgstPct.toFixed(2) : '0.00'}%)
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {formatCurrency(Number(billSummary?.cgstAmount ?? 0), bootstrap?.hotel?.currency)}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" gap={1}>
                    <Typography variant="body2" sx={{ color: isDark ? '#9fbbd2' : '#607086' }}>
                      SGST ({Number.isFinite(sgstPct) ? sgstPct.toFixed(2) : '0.00'}%)
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {formatCurrency(Number(billSummary?.sgstAmount ?? 0), bootstrap?.hotel?.currency)}
                    </Typography>
                  </Stack>
                  <Divider sx={{ borderColor: alpha(ACCENT, 0.2) }} />
                  <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                    <Typography sx={{ fontWeight: 800 }}>Final payable</Typography>
                    <Typography sx={{ fontWeight: 900, color: ACCENT, fontSize: '1.1rem' }}>
                      {formatCurrency(Number(billSummary?.totalPayable ?? 0), bootstrap?.hotel?.currency)}
                    </Typography>
                  </Stack>
                </Stack>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  p: { xs: 1.3, sm: 1.6 },
                  borderRadius: 2,
                  border: `1px solid ${alpha(ACCENT, 0.24)}`,
                  bgcolor: isDark ? alpha('#111827', 0.9) : '#ffffff',
                }}
              >
                <Stack spacing={1.1}>
                  <Typography sx={{ fontWeight: 800, fontSize: 18 }}>Guest details</Typography>
                  <TextField
                    fullWidth
                    size="small"
                    label="Name (optional)"
                    value={customerForm.customer_name}
                    onChange={(event) => setCustomerForm((prev) => ({
                      ...prev,
                      customer_name: event.target.value,
                    }))}
                  />
                  <TextField
                    fullWidth
                    required
                    size="small"
                    label="Mobile number"
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel"
                    placeholder="10-digit mobile number"
                    value={customerForm.customer_phone}
                    onChange={(event) => {
                      const digitsOnly = String(event.target.value || '').replace(/\D/g, '').slice(0, 10);
                      setCustomerForm((prev) => ({
                        ...prev,
                        customer_phone: digitsOnly,
                      }));
                    }}
                    onBlur={() => setPhoneTouched(true)}
                    error={phoneTouched && !isPhoneValid}
                    helperText={phoneHelperText}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    minRows={2}
                    label="Order notes (optional)"
                    value={customerForm.notes}
                    onChange={(event) => setCustomerForm((prev) => ({
                      ...prev,
                      notes: event.target.value,
                    }))}
                  />
                </Stack>
              </Paper>

            </Stack>
          )}
        </Stack>
      </Box>

      {hasCartItems ? (
        <Paper
          elevation={0}
          sx={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1300,
            width: '100%',
            px: { xs: 1, sm: 1.8, md: 3, lg: 4 },
            pt: { xs: 0.9, sm: 1 },
            pb: { xs: 'calc(10px + env(safe-area-inset-bottom, 0px))', sm: 'calc(10px + env(safe-area-inset-bottom, 0px))' },
            borderTop: `1px solid ${alpha(ACCENT, 0.34)}`,
            borderRadius: 0,
            bgcolor: isDark ? alpha('#0b1626', 0.99) : alpha('#fff8f2', 0.99),
            backdropFilter: 'blur(10px)',
          }}
        >
          <Box
            sx={{
              width: '100%',
              maxWidth: { xs: 620, md: 'min(1600px, 100%)' },
              mx: 'auto',
            }}
          >
            <Button
              fullWidth
              variant="contained"
              startIcon={<PaymentsIcon />}
              onClick={handlePlaceOrder}
              disabled={submittingOrder || !hasCartItems}
              sx={{
                textTransform: 'none',
                borderRadius: 1.6,
                minHeight: { xs: 56, sm: 60 },
                fontSize: { xs: '1.08rem', sm: '1.14rem' },
                fontWeight: 900,
                background: 'linear-gradient(90deg, #4ce1b6 0%, #2dd4a0 100%)',
                color: '#06251e',
                boxShadow: `0 4px 14px ${alpha(ACCENT, 0.35)}`,
                '&:hover': {
                  background: 'linear-gradient(90deg, #3dd1a8 0%, #22c997 100%)',
                  boxShadow: `0 6px 18px ${alpha(ACCENT, 0.42)}`,
                },
              }}
            >
              {submittingOrder ? 'Placing...' : 'Place Order'}
            </Button>
          </Box>
        </Paper>
      ) : null}
      <Dialog
        open={occupiedDialogOpen}
        onClose={() => setOccupiedDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            background: isDark
              ? 'linear-gradient(160deg, rgba(20,31,37,0.98) 0%, rgba(10,14,22,0.98) 100%)'
              : 'linear-gradient(160deg, #ffffff 0%, #f5fffb 100%)',
            boxShadow: `0 0 32px ${alpha(ACCENT, 0.35)}`,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 900, fontSize: { xs: 24, sm: 30 }, textAlign: 'center', pb: 1 }}>
          Oops
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', px: { xs: 2.2, sm: 3.5 } }}>
          <Box sx={{ position: 'relative', mb: 2.2 }}>
            <Box
              component="img"
              src={OCCUPIED_TABLE_POPUP_IMAGE}
              alt="Guests ordering at a restaurant table"
              sx={{
                width: '100%',
                maxHeight: { xs: 260, sm: 330 },
                objectFit: 'cover',
                borderRadius: 3,
                display: 'block',
                boxShadow: `0 16px 34px ${alpha('#000', isDark ? 0.45 : 0.18)}`,
                animation: 'occupiedImageFloat 2.8s ease-in-out infinite',
                '@keyframes occupiedImageFloat': {
                  '0%, 100%': { transform: 'translateY(0)' },
                  '50%': { transform: 'translateY(-5px)' },
                },
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                top: 14,
                right: 14,
                width: 46,
                height: 46,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                fontSize: 26,
                fontWeight: 900,
                color: '#06251e',
                bgcolor: ACCENT,
                boxShadow: `0 0 0 0 ${alpha(ACCENT, 0.55)}`,
                animation: 'occupiedPulse 1.4s ease-out infinite',
                '@keyframes occupiedPulse': {
                  '0%': { boxShadow: `0 0 0 0 ${alpha(ACCENT, 0.55)}`, transform: 'scale(1)' },
                  '70%': { boxShadow: `0 0 0 16px ${alpha(ACCENT, 0)}`, transform: 'scale(1.06)' },
                  '100%': { boxShadow: `0 0 0 0 ${alpha(ACCENT, 0)}`, transform: 'scale(1)' },
                },
              }}
            >
              !
            </Box>
          </Box>
          <Typography sx={{ fontSize: { xs: 18, sm: 22 }, fontWeight: 800, lineHeight: 1.35 }}>
            Oops, this table is already occupied.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'center' }}>
          <Button
            variant="contained"
            onClick={() => setOccupiedDialogOpen(false)}
            sx={{
              textTransform: 'none',
              fontWeight: 900,
              px: 4,
              minHeight: 44,
              borderRadius: 2,
              background: 'linear-gradient(90deg, #4ce1b6 0%, #2dd4a0 100%)',
              color: '#06251e',
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
